// client/src/components/NotificationsPanel.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('ipe_token');
const authHeaders = (extra) => ({ 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}), ...extra });
const TYPE_CONFIG = {
  tender_review:       { icon: '\ud83d\udccb', label: 'Tender Review',     colour: '#0e9aaa' },
  cr_required:         { icon: '\ud83d\udcdd', label: 'Contract Review',   colour: '#f59e0b' },
  cr_overdue_48hr:     { icon: '\u26a0',       label: 'CR Overdue',        colour: '#ef4444' },
  cr_overdue_repeat:   { icon: '\ud83d\udd34', label: 'CR Still Overdue',  colour: '#ef4444' },
  bid_no_bid:          { icon: '\u2696',       label: 'Bid/No Bid',        colour: '#8b5cf6' },
  won_job:             { icon: '\ud83c\udfc6', label: 'Job Won',           colour: '#10b981' },
  submission_deadline: { icon: '\u23f0',       label: 'Deadline Alert',    colour: '#f59e0b' },
};
const FORCED_TYPES = ['submission_deadline'];

function timeAgo(ts) {
  const d = Date.now() - new Date(ts).getTime();
  const m = Math.floor(d/60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m/60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h/24)}d ago`;
}

function NotifItem({ n, role, onRead, onSnooze }) {
  const cfg = TYPE_CONFIG[n.type] || { icon:'\ud83d\udd14', label:n.type, colour:'#94a3b8' };
  const forced = FORCED_TYPES.includes(n.type) && role === 'estimator';
  const [picker, setPicker] = useState(false);
  return (
    <div style={{ padding:'12px 14px', borderBottom:'1px solid #1e3a5f', background: n.is_read ? 'transparent' : 'rgba(14,154,170,0.04)' }}>
      <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
        <div style={{ width:6, height:6, borderRadius:'50%', flexShrink:0, marginTop:5, background: n.is_read ? 'transparent' : cfg.colour }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
            <span style={{ fontSize:13 }}>{cfg.icon}</span>
            <span style={{ fontSize:11, fontWeight:600, color:cfg.colour, textTransform:'uppercase', letterSpacing:'0.04em' }}>{cfg.label}</span>
            <span style={{ fontSize:11, color:'#475569', marginLeft:'auto', flexShrink:0 }}>{timeAgo(n.created_at)}</span>
          </div>
          <div style={{ fontSize:13, color:'#e2e8f0', fontWeight:500, marginBottom:2 }}>{n.title}</div>
          {n.body && <div style={{ fontSize:12, color:'#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={n.body}>{n.body}</div>}
          {n.link_label && (
            <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>
              {n.link_reference && <span style={{ color:'#0e9aaa', fontFamily:'monospace' }}>{n.link_reference} \u2014 </span>}
              {n.link_label}
            </div>
          )}
          <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
            {!n.is_read && (
              <button onClick={() => onRead(n.id)} style={{ padding:'3px 9px', borderRadius:4, fontSize:11, border:'1px solid #334155', background:'transparent', color:'#94a3b8', cursor:'pointer' }}>Mark read</button>
            )}
            {!forced && (
              <div style={{ position:'relative' }}>
                <button onClick={() => setPicker(v=>!v)} style={{ padding:'3px 9px', borderRadius:4, fontSize:11, border:'1px solid #334155', background:'transparent', color:'#94a3b8', cursor:'pointer' }}>Snooze</button>
                {picker && (
                  <div style={{ position:'absolute', top:'100%', left:0, zIndex:100, background:'#1e293b', border:'1px solid #334155', borderRadius:6, padding:6, marginTop:4, minWidth:130 }}>
                    {[{l:'1 hour',h:1},{l:'4 hours',h:4},{l:'24 hours',h:24}].map(o => (
                      <button key={o.h} onClick={() => { onSnooze(n.id, o.h); setPicker(false); }}
                        style={{ display:'block', width:'100%', padding:'5px 8px', textAlign:'left', border:'none', background:'transparent', color:'#cbd5e1', cursor:'pointer', fontSize:12, borderRadius:4 }}
                        onMouseEnter={e=>e.target.style.background='rgba(14,154,170,0.1)'}
                        onMouseLeave={e=>e.target.style.background='transparent'}
                      >{o.l}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {forced && (
              <span style={{ padding:'3px 9px', borderRadius:4, fontSize:11, background:'rgba(239,68,68,0.1)', color:'#f87171', border:'1px solid rgba(239,68,68,0.25)' }}>Action required to dismiss</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NotificationsPanel({ currentUser }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);
  const poll = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    poll.current = setInterval(load, 60000);
    return () => clearInterval(poll.current);
  }, [load]);

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  async function handleRead(id) {
    await fetch(`${API}/api/notifications/${id}/read`, { method:'PATCH', headers: authHeaders() });
    load();
  }
  async function handleReadAll() {
    await fetch(`${API}/api/notifications/read-all`, { method:'PATCH', headers: authHeaders() });
    load();
  }
  async function handleSnooze(id, hours) {
    await fetch(`${API}/api/notifications/${id}/snooze`, {
      method:'PATCH', headers: authHeaders(),
      headers:{'Content-Type':'application/json'}, body:JSON.stringify({ hours })
    });
    load();
  }

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button onClick={() => { setOpen(v=>!v); }} title="Notifications"
        style={{ position:'relative', background:'transparent', border:'none', cursor:'pointer', padding:'6px 8px', borderRadius:6, color: unreadCount > 0 ? '#0e9aaa' : '#64748b', display:'flex', alignItems:'center' }}>
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={{ position:'absolute', top:2, right:2, background:'#ef4444', color:'#fff', borderRadius:'50%', fontSize:9, fontWeight:700, minWidth:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px' }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 8px)', right:0, zIndex:500, width:360, maxWidth:'90vw', background:'#1e293b', border:'1px solid #1e3a5f', borderRadius:10, boxShadow:'0 16px 48px rgba(0,0,0,0.35)', display:'flex', flexDirection:'column', maxHeight:'70vh', overflow:'hidden' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', borderBottom:'1px solid #1e3a5f', flexShrink:0 }}>
            <span style={{ fontSize:14, fontWeight:600, color:'#f1f5f9' }}>
              Notifications {unreadCount > 0 && <span style={{ fontSize:11, color:'#64748b', fontWeight:400 }}>({unreadCount} unread)</span>}
            </span>
            {unreadCount > 0 && (
              <button onClick={handleReadAll} style={{ padding:'3px 9px', borderRadius:4, fontSize:11, border:'1px solid #334155', background:'transparent', color:'#94a3b8', cursor:'pointer' }}>Mark all read</button>
            )}
          </div>
          <div style={{ overflowY:'auto', flex:1 }}>
            {notifications.length === 0
              ? <div style={{ padding:'32px 16px', textAlign:'center', color:'#64748b', fontSize:13 }}>No notifications</div>
              : notifications.map(n => (
                  <NotifItem key={n.id} n={n} role={currentUser?.role} onRead={handleRead} onSnooze={handleSnooze} />
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
}
